import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { logout } from "../redux/slices/authSlice";
import {
  expireSession,
  getIdleTimeoutMs,
  getTokenExpiration,
  isTokenExpired,
  SESSION_EXPIRED_EVENT,
  setupAxiosSessionInterceptor,
} from "../utils/session";

const activityEvents = [
  "click",
  "keydown",
  "mousemove",
  "mousedown",
  "scroll",
  "touchstart",
];

const useSessionManager = () => {
  const dispatch = useDispatch();
  const { token, isAuthenticated } = useSelector((state) => state.auth);

  useEffect(() => {
    setupAxiosSessionInterceptor();
  }, []);

  useEffect(() => {
    const handleSessionExpired = () => {
      dispatch(logout());
    };

    window.addEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
    return () => {
      window.removeEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
    };
  }, [dispatch]);

  useEffect(() => {
    if (!token) {
      return undefined;
    }

    if (isTokenExpired(token)) {
      expireSession("expired");
      return undefined;
    }

    const expiresAt = getTokenExpiration(token);
    if (!expiresAt) {
      return undefined;
    }

    const timeoutId = window.setTimeout(
      () => expireSession("expired"),
      Math.max(expiresAt - Date.now(), 0),
    );

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [token]);

  useEffect(() => {
    if (!isAuthenticated) {
      return undefined;
    }

    let idleTimeoutId;

    const resetIdleTimer = () => {
      window.clearTimeout(idleTimeoutId);
      idleTimeoutId = window.setTimeout(
        () => expireSession("idle"),
        getIdleTimeoutMs(),
      );
    };

    resetIdleTimer();
    activityEvents.forEach((eventName) => {
      window.addEventListener(eventName, resetIdleTimer, { passive: true });
    });

    return () => {
      window.clearTimeout(idleTimeoutId);
      activityEvents.forEach((eventName) => {
        window.removeEventListener(eventName, resetIdleTimer);
      });
    };
  }, [isAuthenticated]);
};

export default useSessionManager;
